import React, { useState, useEffect, useCallback, useMemo, useRef, startTransition } from "react";
import { MomentumScore } from "@/api/entities";
import { PersonaActivity } from "@/api/entities";
import { AccountFinal } from "@/api/entities";
import { Procurement } from "@/api/entities";
import { PersonFinal } from "@/api/entities";
import { AlertPreference } from "@/api/entities";
import { Badge } from "@/components/ui/badge";
import { format, subWeeks, isAfter, parseISO } from "date-fns";
import { notify } from "@/components/ui/notify";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import {
  isInRateLimitCooldown,
  setRateLimitCooldown,
  clearRateLimitCooldown,
  shouldShowRateLimitToast,
  markRateLimitToastShown,
  formatRemainingTime,
  getRemainingCooldownTime
} from "@/components/utils/rateLimitHandler";
import CountryFilter from "../components/filters/CountryFilter";
import PlaybookIndicator from "../components/PlaybookIndicator";
import { PlaybookStatusProvider } from "@/components/PlaybookStatusContext";
import { useTranslation, resolvePlatformUiLanguage } from '@/components/utils/translations';

import ActionCenterWithPlan from "../components/home/ActionCenterWithPlan";
import TopPersonasCard from "../components/home/TopPersonasCard";
import TopAccountsCard from "../components/home/TopAccountsCard";
import PersonAvatar from '@/components/PersonAvatar';
import OutboxDrawer from '../components/outbox/OutboxDrawer';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { useUser } from '@/contexts/UserContext';
import { expandAlertCategoriesForMatching } from '@/lib/alertCategoryNames';
import { addPlanItemToAckSet, limitTodaysPlanForDisplay, planItemKey } from '@/lib/todaysPlanUtils';
import {
  probeMarketModule,
  fetchMarketLenses,
  fetchMarketOnboarding,
} from '@/lib/marketExperienceApi';
import { Button } from '@/components/ui/button';
import { personaFitBand, PERSONA_FIT_LABELS } from '@/lib/personaFitLabel';

const API_BASE_URL = (() => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'https://api.neogtm.com';
    }
  }
  return 'https://api.neogtm.com';
})();
const HOME_CACHE_KEY = 'home-cache-v6'; // v6: persona reporting floor + no legacy score fallback
const PLAN_CACHE_KEY = 'plan-cache-v3';
const ACK_KEY_PREFIX = 'tp-ack-v1:';
const PLAN_CACHE_DURATION = 12 * 60 * 60 * 1000;
const CACHE_DURATION = 20 * 60 * 1000;
const DEFAULT_TENANT = 'instructure';

const fetchWithRetry = async (fetchFn, maxRetries = 3, baseDelay = 2000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error) {
      const isRateLimit = error.response?.status === 429 || error.status === 429 || error.message?.includes('429');
      
      if (isRateLimit) {
        console.log(`🚫 Rate limit hit on attempt ${attempt}/${maxRetries}`);
        
        if (attempt === maxRetries) {
          throw error;
        }
        
        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.log(`⏳ Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        throw error;
      }
    }
  }
};

const normalizeAccountName = (name) => {
  if (!name) return '';
  return name
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const applyTenantOverrides = (entity, currentTenantId) => {
  if (!entity || !entity.tenant_overrides || !currentTenantId) {
    return entity;
  }
  const override = entity.tenant_overrides[currentTenantId];
  if (override) {
    return { ...entity, ...override };
  }
  return entity;
};

function readHomeCache(currentTenant) {
  try {
    const raw = sessionStorage.getItem(HOME_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    
    // Check if cache is for the current tenant
    if (data.tenantId && data.tenantId !== currentTenant) {
      console.log('📦 Home: Cache is for different tenant, clearing...');
      sessionStorage.removeItem(HOME_CACHE_KEY);
      return null;
    }
    
    if (data.cachedAt && (Date.now() - data.cachedAt > CACHE_DURATION)) {
      console.log('📦 Home: Cache expired, clearing...');
      sessionStorage.removeItem(HOME_CACHE_KEY);
      return null;
    }
    return data;
  } catch { return null; }
}

function writeHomeCache(snapshot, tenantId) {
  try {
    sessionStorage.setItem(
      HOME_CACHE_KEY,
      JSON.stringify({ ...snapshot, tenantId, cachedAt: Date.now() })
    );
  } catch {}
}

function mergeHomeCache(updates, tenantId) {
  const existing = readHomeCache(tenantId) || {};
  writeHomeCache({
    accounts: updates.accounts ?? existing.accounts ?? [],
    people: updates.people ?? existing.people ?? [],
    procurement: updates.procurement ?? existing.procurement ?? [],
    momentumScores: updates.momentumScores ?? existing.momentumScores ?? [],
    personaActivities: updates.personaActivities ?? existing.personaActivities ?? [],
    alertPreferences: updates.alertPreferences ?? existing.alertPreferences ?? null,
  }, tenantId);
}

function getPlanCacheKey(tenantId) {
  return `${PLAN_CACHE_KEY}-${tenantId || 'default'}`;
}

function readPlanCache(tenantId) {
  try {
    const raw = sessionStorage.getItem(getPlanCacheKey(tenantId));
    if (!raw) return null;
    const cached = JSON.parse(raw);
    const isFresh = cached.cachedAt && (Date.now() - cached.cachedAt) < PLAN_CACHE_DURATION;
    return { plan: cached.plan, isFresh };
  } catch { return null; }
}

function writePlanCache(planData, tenantId) {
  try {
    sessionStorage.setItem(
      getPlanCacheKey(tenantId),
      JSON.stringify({ plan: planData, cachedAt: Date.now() })
    );
  } catch {}
}

function readAckSet(userId) {
  try {
    const raw = localStorage.getItem(ACK_KEY_PREFIX + (userId || 'anon'));
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
}

function writeAckSet(userId, ackSet) {
  try {
    localStorage.setItem(ACK_KEY_PREFIX + (userId || 'anon'), JSON.stringify([...ackSet]));
  } catch {}
}

function top5PlanByImpact(todaysPlan, ackSet) {
  return limitTodaysPlanForDisplay(todaysPlan, ackSet, { outreachLimit: 5 });
}

const getGreeting = (displayName, t) => {
  const name = displayName || "there";
  const hour = parseInt(new Date().toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: 'Europe/London' }));
  if (hour < 12) return `${t?.('goodMorning') || 'Good morning'}, ${name}`;
  if (hour < 18) return `${t?.('goodAfternoon') || 'Good afternoon'}, ${name}`;
  return `${t?.('goodEvening') || 'Good evening'}, ${name}`;
};

function patchPlanTracked(plan, personaId, on) {
  if (!plan) return plan;
  const next = {
    updatedAt: plan.updatedAt,
    planByCategory: {},
    flat: Array.isArray(plan.flat)
      ? plan.flat.map(it => (it.personaId === personaId || it.person_id === personaId) ? ({ ...it, metadata: { ...(it.metadata||{}), addToPursuit: on }}) : it)
      : []
  };
  for (const [cat, items] of Object.entries(plan.planByCategory || {})) {
    next.planByCategory[cat] = items.map(it =>
      (it.personaId === personaId || it.person_id === personaId)
        ? ({ ...it, metadata: { ...(it.metadata||{}), addToPursuit: on }})
        : it
    );
  }
  return next;
}

const createPageUrl = (path) => {
  return path.startsWith('/') ? path : `/${path}`;
};

export default function Home({ startChatSession, currentTenant }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const userId = contextUser?.id || null;
  const userRole = contextUser?.role || null;
  const userTenantId = contextUser?.tenant_id || null;
  const isUserReady = !!contextUser && (!!contextUser.id || !!contextUser.email);

  const [accounts, setAccounts] = useState([]);
  const [people, setPeople] = useState([]);
  const [procurement, setProcurement] = useState([]);
  const [momentumScores, setMomentumScores] = useState([]);
  const [personaActivities, setPersonaActivities] = useState([]);
  const [alertPreferences, setAlertPreferences] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [hydratedFromCache, setHydratedFromCache] = useState(false);

  const [todaysPlan, setTodaysPlan] = useState(null);
  const [isPlanLoading, setIsPlanLoading] = useState(true);
  const [isPlanRefreshing, setIsPlanRefreshing] = useState(false);

  const [ackSet, setAckSet] = useState(() => new Set());
  const [selectedDraftForDrawer, setSelectedDraftForDrawer] = useState(null);
  const [selectedTaskForDrawer, setSelectedTaskForDrawer] = useState(null);
  const [filters, setFilters] = useState({ country: "all" });
  const [marketOnboarding, setMarketOnboarding] = useState(null);

  const aliveRef = useRef(true);
  const todaysPlanRef = useRef(todaysPlan);
  const loadAttemptedRef = useRef(false);
  const dataLoadedRef = useRef(false);
  const lastLoadedTenantRef = useRef(null);
  const accountsRef = useRef([]);
  const peopleRef = useRef([]);

  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  useEffect(() => {
    peopleRef.current = people;
  }, [people]);

  const effectiveTenant = useMemo(() => {
    const adminTenant = userRole === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
    return resolveEffectiveTenant(adminTenant, currentTenant, contextUser, 'instructure');
  }, [currentTenant, contextUser, userRole]);

  const startChatSessionWithTenant = useCallback((context = {}) => {
    const adminActive = userRole === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
    const tenantId = resolveEffectiveTenant(adminActive, currentTenant, contextUser, 'instructure');
    const enrichedContext = { ...context, tenant_id: tenantId };
    startChatSession(enrichedContext);
  }, [startChatSession, currentTenant, contextUser, userRole]);

  useEffect(() => {
    if (dataLoadedRef.current) return;
    
    // Don't hydrate from cache until we know the user's role
    // This prevents non-admin users from using admin-active-tenant
    const userRole = localStorage.getItem('user-role');
    const adminTenant =
      userRole === 'admin'
        ? localStorage.getItem('admin-active-tenant')
        : null;
    const effectiveTenantForCache = adminTenant || (currentTenant || DEFAULT_TENANT);
    const cached = readHomeCache(effectiveTenantForCache);
    
    if (cached) {
      console.log('📦 Home: Hydrating from cache for tenant:', effectiveTenantForCache);
      
      // Build tenantMatches function to filter cached data
      const tenantMatches = (record) => {
        if (!record) return false;
        if (effectiveTenantForCache === 'smowl') return record.tenant_id === 'smowl';
        if (record.tenant_id === effectiveTenantForCache || record.tenant_id === 'global') return true;
        if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(effectiveTenantForCache)) return true;
        if (record.tenant_overrides && record.tenant_overrides[effectiveTenantForCache]) return true;
        if (effectiveTenantForCache === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id || record.tenant_id === 'clairelabs')) return true;
        if (effectiveTenantForCache === 'diambra') return record.tenant_id === 'diambra' || record.tenant_id === 'global';
        if (effectiveTenantForCache === 'edqwest') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
        if (effectiveTenantForCache === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
        if (effectiveTenantForCache === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
        if (effectiveTenantForCache === 'prowise') return record.tenant_id === 'prowise' || record.tenant_id === 'global';
        if (effectiveTenantForCache === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise' || record.tenant_id === 'global';
        if (effectiveTenantForCache === 'kairadigital') return record.tenant_id === 'kairadigital';
        if (effectiveTenantForCache === 'plataformaa') return record.tenant_id === 'plataformaa';
        return false;
      };
      
      // Filter cached data to ensure tenant isolation
      const filteredAccounts = (cached.accounts || []).filter(acc => tenantMatches(acc) && acc.is_visible !== false);
      const filteredPeople = (cached.people || []).filter(p => tenantMatches(p) && p.is_visible !== false);
      const filteredProcurement = (cached.procurement || []).filter(p => tenantMatches(p));
      const filteredMomentum = (cached.momentumScores || []).filter(m => tenantMatches(m));
      const filteredActivities = (cached.personaActivities || []).filter(a => tenantMatches(a));
      
      if (filteredAccounts.length !== (cached.accounts || []).length || 
          filteredPeople.length !== (cached.people || []).length) {
        console.warn('⚠️ Home: Filtered out cached data that doesn\'t match tenant:', effectiveTenantForCache, {
          accounts: (cached.accounts || []).length - filteredAccounts.length,
          people: (cached.people || []).length - filteredPeople.length
        });
      }
      
      setAccounts(filteredAccounts);
      setPeople(filteredPeople);
      setProcurement(filteredProcurement);
      setMomentumScores(filteredMomentum);
      setPersonaActivities(filteredActivities);
      setAlertPreferences(cached.alertPreferences || null);
      setHydratedFromCache(true);
      setIsLoading(false);
      dataLoadedRef.current = true;
    }
  }, [currentTenant]);

  useEffect(() => {
    const cachedPlan = readPlanCache(effectiveTenant);
    if (cachedPlan && cachedPlan.plan) {
      setTodaysPlan(cachedPlan.plan);
      setIsPlanLoading(false);
    }
  }, [effectiveTenant]);

  useEffect(() => {
    todaysPlanRef.current = todaysPlan;
  }, [todaysPlan]);

  useEffect(() => {
    if (userId) {
      setAckSet(readAckSet(userId));
    }
  }, [userId]);

  useEffect(() => {
    const handler = (event) => {
      const prefs = event.detail;
      console.log('🔔 HOME: preferences updated via event', prefs);
      setAlertPreferences(prefs);
    };

    window.addEventListener('alert-preferences-updated', handler);
    return () => window.removeEventListener('alert-preferences-updated', handler);
  }, []);

  const loadDashboardData = useCallback(async () => {
    if (!contextUser?.id && !contextUser?.email) {
      console.log('Home: user context not ready yet, skipping dashboard load');
      return;
    }

    const adminTenant = userRole === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
    const resolvedTenant = resolveEffectiveTenant(adminTenant, currentTenant, contextUser, DEFAULT_TENANT);

    if (loadAttemptedRef.current) {
      console.log('⏭️ Load already in progress, skipping');
      return;
    }

    // Load alert preferences only once per day
    if (userId) {
      try {
        const adminActiveTenant = userRole === 'admin' ? localStorage.getItem("admin-active-tenant") : null;
        const tenantId = resolveEffectiveTenant(adminActiveTenant, null, contextUser, 'instructure');
        const cacheKey = `alert-preferences-cache-${userId}-${tenantId}`;
        const loadKey = `last-alert-prefs-load-${userId}-${tenantId}`;
        const lastAlertLoad = localStorage.getItem(loadKey);
        const cachedPrefs = localStorage.getItem(cacheKey);
        const now = Date.now();
        const oneDayMs = 24 * 60 * 60 * 1000;

        if (!lastAlertLoad || (now - parseInt(lastAlertLoad)) > oneDayMs) {
          const prefsArray = await fetchWithRetry(() =>
            AlertPreference.filter({ user_id: userId, tenant_id: tenantId })
          );
          const userPrefs = prefsArray[0] || null;
          setAlertPreferences(userPrefs);
          localStorage.setItem(loadKey, now.toString());
          if (userPrefs) localStorage.setItem(cacheKey, JSON.stringify(userPrefs));
        } else if (cachedPrefs) {
          setAlertPreferences(JSON.parse(cachedPrefs));
        }
      } catch (error) {
        console.warn('Could not refresh alert preferences:', error);
      }
    }
    if (dataLoadedRef.current &&
        accountsRef.current.length > 0 &&
        peopleRef.current.length > 0 &&
        lastLoadedTenantRef.current === resolvedTenant) {
      console.log('✅ Data already loaded for current tenant, skipping heavy reload (prefs refreshed separately)');
      return;
    }

    console.log('🔄 Starting data load...');
    loadAttemptedRef.current = true;

    if (isInRateLimitCooldown()) {
      const cached = readHomeCache(resolvedTenant);
      if (cached && cached.accounts && cached.accounts.length > 0) {
        const remaining = getRemainingCooldownTime();
        console.log(`⏸️ In cooldown (${formatRemainingTime(remaining)}), using cached data`);

        // Build tenantMatches function to filter cached data
        const tenantMatches = (record) => {
          if (!record) return false;
          if (resolvedTenant === 'smowl') return record.tenant_id === 'smowl';
          if (record.tenant_id === resolvedTenant || record.tenant_id === 'global') return true;
          if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(resolvedTenant)) return true;
          if (record.tenant_overrides && record.tenant_overrides[resolvedTenant]) return true;
          if (resolvedTenant === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id || record.tenant_id === 'clairelabs')) return true;
          if (resolvedTenant === 'diambra') return record.tenant_id === 'diambra' || record.tenant_id === 'global';
          if (resolvedTenant === 'edqwest') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
          if (resolvedTenant === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
          if (resolvedTenant === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
          if (resolvedTenant === 'prowise') return record.tenant_id === 'prowise' || record.tenant_id === 'global';
          if (resolvedTenant === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise' || record.tenant_id === 'global';
          if (resolvedTenant === 'kairadigital') return record.tenant_id === 'kairadigital';
          if (resolvedTenant === 'plataformaa') return record.tenant_id === 'plataformaa';
          return false;
        };
        
        // Filter cached data to ensure tenant isolation
        const filteredAccounts = (cached.accounts || []).filter(acc => tenantMatches(acc) && acc.is_visible !== false);
        const filteredPeople = (cached.people || []).filter(p => tenantMatches(p) && p.is_visible !== false);
        const filteredProcurement = (cached.procurement || []).filter(p => tenantMatches(p));
        const filteredMomentum = (cached.momentumScores || []).filter(m => tenantMatches(m));
        const filteredActivities = (cached.personaActivities || []).filter(a => tenantMatches(a));

        setAccounts(filteredAccounts);
        setPeople(filteredPeople);
        setProcurement(filteredProcurement);
        setMomentumScores(filteredMomentum);
        setPersonaActivities(filteredActivities);
        setHydratedFromCache(true);
        setIsLoading(false);
        dataLoadedRef.current = true;

        if (shouldShowRateLimitToast()) {
          notify({
            title: "⏸️ Rate limit active",
            description: `Using cached data. Will retry in ${formatRemainingTime(remaining)}.`,
            variant: "default",
            duration: 8000
          });
          markRateLimitToastShown();
        }
        loadAttemptedRef.current = false;
        return;
      }
    }

    if (!hydratedFromCache && accounts.length === 0) {
      setIsLoading(true);
    }

    try {
      const tenantId = resolvedTenant;
      
      console.log(`📊 Loading Home for tenant: ${tenantId}`);
      
      // Build tenant filter query to match Market.jsx logic
      let tenantFilter;
      if (tenantId === 'instructure') {
        tenantFilter = { $in: ['instructure', 'clairelabs', 'default', 'global'] };
      } else if (tenantId === 'smowl') {
        tenantFilter = { $in: ['smowl'] };
      } else if (tenantId === 'diambra') {
        tenantFilter = { $in: ['diambra', 'global'] };
      } else if (tenantId === 'clairelabs') {
        tenantFilter = { $in: ['clairelabs', 'default', 'global'] };
      } else if (tenantId === 'faria' || tenantId === 'edqwest') {
        tenantFilter = { $in: ['edqwest', 'default', 'global'] };
      } else if (tenantId === 'calmedica') {
        tenantFilter = { $in: [tenantId] };
      } else {
        tenantFilter = { $in: [tenantId, 'default', 'global'] };
      }

      const tenantMatches = (record) => {
        if (!record) return false;
        if (tenantId === 'smowl') return record.tenant_id === 'smowl';
        if (record.tenant_id === tenantId || record.tenant_id === 'global') return true;
        if (tenantId === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id)) return true;
        if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(tenantId)) return true;
        if (record.tenant_overrides && record.tenant_overrides[tenantId]) return true;
        if (tenantId === 'diambra') return record.tenant_id === 'diambra' || record.tenant_id === 'global';
        if (tenantId === 'edqwest') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
        if (tenantId === 'faria') return record.tenant_id === 'faria' || record.tenant_id === 'edqwest' || record.tenant_id === 'global';
        if (tenantId === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
        if (tenantId === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
        if (tenantId === 'prowise') return record.tenant_id === 'prowise' || record.tenant_id === 'global';
        if (tenantId === 'kairadigital') return record.tenant_id === 'kairadigital';
        return false;
      };

      // ✅ Kick off people fetch early (parallel with accounts)
      const peoplePromise = fetchWithRetry(() =>
        PersonFinal.filter({ tenant_id: tenantFilter }, "-persona_fit_score", 500)
      );

      const [allAccountData, allProcurementData] = await Promise.all([
        fetchWithRetry(() => AccountFinal.filter({ tenant_id: tenantFilter }, "-account_fit_score", 500)),
        fetchWithRetry(() => Procurement.filter({ status: "Live", tenant_id: tenantFilter }, "-deadline", 300))
      ]);

      const filteredAccountsInitial = allAccountData.filter(tenantMatches).filter(acc => acc.is_visible !== false);
      const filteredProcurementInitial = allProcurementData.filter(p => {
        if (p.status === 'Live' || p.tenant_id === 'global') return true;
        return tenantMatches(p);
      });

      // ✅ FAST: load personas as soon as we know visible accounts (Market-style)
const hongKongAccounts = [
  'City University of Hong Kong (CityU)',
  'The Hong Kong University of Science and Technology (HKUST)'
];

peoplePromise
  .then(async (personFinalData) => {
    if (!aliveRef.current) return;
    
    // Guard against stale requests from previous tenant
    if (tenantId !== lastLoadedTenantRef.current) {
      console.log('⚠️ Discarding stale people data, tenant changed from', tenantId, 'to', lastLoadedTenantRef.current);
      return;
    }

    const visibleAccountNames = new Set(filteredAccountsInitial.map(a => normalizeAccountName(a.account_name)));
    const filteredPeopleFast = (personFinalData || [])
      .filter(person => {
        if (!tenantMatches(person)) return false;
        if (person.is_visible === false) return false;
        if (!person.account_name) return false;
        // HK filter for instructure
        if (tenantId === 'instructure' && hongKongAccounts.includes(person.account_name)) return false;
        return visibleAccountNames.has(normalizeAccountName(person.account_name));
      })
      .map(person => {
        if (person.tenant_overrides && person.tenant_overrides[tenantId]) {
          return { ...person, ...person.tenant_overrides[tenantId] };
        }
        return person;
      });

    console.log('⚡ Fast people load:', filteredPeopleFast.length, 'personas');

    // ⚡ Show Top Personas immediately
    startTransition(() => {
      setPeople(filteredPeopleFast);
    });

    mergeHomeCache({
      accounts: filteredAccountsInitial,
      people: filteredPeopleFast,
      procurement: filteredProcurementInitial,
    }, tenantId);

    // Merge pursuit status AFTER rendering (don't block Top Personas)
    try {
      const pursuitRes = await fetch(
        `${API_BASE_URL}/api/diy-playbooks/personas-in-pursuit?tenant_id=${tenantId}&user_id=${userId}`,
        { credentials: 'include' }
      );

      if (pursuitRes.ok) {
        const pursuitData = await pursuitRes.json();
        const pursuitArray = pursuitData.personas || pursuitData || [];
        const pursuitIds = new Set(pursuitArray.map(p => p.person_id));

        const withPursuit = filteredPeopleFast.map(p => ({
          ...p,
          add_to_pursuit: pursuitIds.has(p.person_id) || pursuitIds.has(p.id)
        }));

        startTransition(() => {
          setPeople(withPursuit);
        });

        mergeHomeCache({ people: withPursuit }, tenantId);
        
        console.log('✅ Merged pursuit status (fast path):', pursuitIds.size, 'in pursuit');
      }
    } catch (e) {
      console.warn('Could not load pursuit status (fast path):', e);
    }
  })
  .catch(err => console.warn('People fast-load failed:', err));

      // 🧪 DEBUG: Check all LMS/SIS/Remote Proctoring deals with deadlines
      console.log('🧪 DEBUG all live LMS-like deals with deadlines:', 
        filteredProcurementInitial
          .filter(p =>
            ['Learning Management Systems (LMS)', 'Student Information Systems (SIS)', 'Remote Proctoring']
              .includes(p.category)
          )
          .map(p => ({
            title: p.title,
            category: p.category,
            deadline: p.deadline,
            tenant_id: p.tenant_id
          }))
          .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
      );

      clearRateLimitCooldown();

      if (!aliveRef.current) return;

      startTransition(() => {
        if (!aliveRef.current) return;
        setAccounts(filteredAccountsInitial);
        setProcurement(filteredProcurementInitial);

        mergeHomeCache({
          accounts: filteredAccountsInitial,
          procurement: filteredProcurementInitial,
          alertPreferences
        }, tenantId);
        
        setIsLoading(false);
        setHydratedFromCache(true);
        dataLoadedRef.current = true;
        lastLoadedTenantRef.current = tenantId;
      });

      Promise.all([
        // Let the backend apply entitlement-driven partition scope (incl. global_temp for V2).
        fetchWithRetry(() => MomentumScore.filter({}, "-date_published", 500)),
        fetchWithRetry(() => PersonaActivity.filter({}, "-date_published", 10000))
      ]).then(async ([momentumData, personaActivityData]) => {
        if (!aliveRef.current) return;
        
        // 🔹 Filter out specific HK accounts for instructure
        const hongKongAccounts = [
          'City University of Hong Kong (CityU)',
          'The Hong Kong University of Science and Technology (HKUST)'
        ];

        // `global_temp` is the shared institutional signal layer — accept rows the
        // backend already scoped to this tenant; do not reuse account tenantMatches alone.
        const signalTenantMatches = (record) => (
          record?.tenant_id === 'global_temp' || tenantMatches(record)
        );
        
        const filteredMomentum = momentumData
          .filter(signalTenantMatches)
          .filter(m => {
            // Exclude specific Hong Kong accounts for instructure
            if (tenantId === 'instructure' && hongKongAccounts.includes(m.institution_name)) {
              return false;
            }
            return true;
          });
        
        console.log('📊 Filtered momentum scores:', {
          total: momentumData.length,
          afterTenantFilter: momentumData.filter(signalTenantMatches).length,
          afterCountryFilter: filteredMomentum.length,
          tenant: tenantId,
        });
        
        // 🔹 Filter persona activities
        const filteredActivities = personaActivityData
          .filter(signalTenantMatches)
          .filter(activity => {
            // Exclude specific Hong Kong accounts for instructure
            if (tenantId === 'instructure' && hongKongAccounts.includes(activity.account_name)) return false;
            return true;
          });

        console.log('📊 Filtered persona activities:', {
          total: personaActivityData.length,
          afterTenantFilter: personaActivityData.filter(signalTenantMatches).length,
          afterCountryFilter: filteredActivities.length,
  tenant: tenantId
});

        startTransition(() => {
          if (!aliveRef.current) return;
          setMomentumScores(filteredMomentum);
          setPersonaActivities(filteredActivities);
          mergeHomeCache({
            momentumScores: filteredMomentum,
            personaActivities: filteredActivities,
          }, tenantId);
          console.log('✅ Background data (momentum + activities) loaded');
        });
      }).catch(err => {
        if (!aliveRef.current) return;
        console.warn('Background data load failed:', err);
      });
      
    } catch (error) {
      if (!aliveRef.current) return;
      console.error('❌ Error loading dashboard data:', error);

      const isRateLimit = error.response?.status === 429 || error.status === 429 || error.message?.includes('429');

      if (isRateLimit) {
        console.log('🚫 Rate limit detected, setting cooldown');
        setRateLimitCooldown();

        const tenantId = resolvedTenant;
        const cached = readHomeCache(tenantId);
        if (cached && cached.accounts && cached.accounts.length > 0) {
          console.log('📦 Using cached data due to rate limit');
          setAccounts(cached.accounts || []);
          setPeople(cached.people || []);
          setProcurement(cached.procurement || []);
          setMomentumScores(cached.momentumScores || []);
          setPersonaActivities(cached.personaActivities || []);
          setHydratedFromCache(true);
          dataLoadedRef.current = true;

          if (shouldShowRateLimitToast()) {
            notify({
              title: "⚠️ Rate limit reached",
              description: "Showing cached data. Will retry automatically in 20 minutes.",
              variant: "default",
              duration: 10000
            });
            markRateLimitToastShown();
          }
        } else {
          if (shouldShowRateLimitToast()) {
            notify({
              title: "⚠️ Rate limit reached",
              description: "No cached data available. Please wait 20 minutes before refreshing.",
              variant: "destructive",
              duration: 15000
            });
            markRateLimitToastShown();
          }
        }
      } else {
        notify({ title: "Error", description: "Could not load dashboard data. Please refresh.", variant: "destructive" });
      }

      setIsLoading(false);
    } finally {
      loadAttemptedRef.current = false;
    }
  }, [contextUser, userId, userRole, currentTenant, effectiveTenant]);

  useEffect(() => {
    aliveRef.current = true;

    if (!isUserReady) return;

    if (effectiveTenant && effectiveTenant !== lastLoadedTenantRef.current) {
      dataLoadedRef.current = false;
      loadAttemptedRef.current = false;
      sessionStorage.removeItem(HOME_CACHE_KEY);
      sessionStorage.removeItem(getPlanCacheKey(lastLoadedTenantRef.current));
      setTodaysPlan(null);
      setIsPlanLoading(true);
      lastLoadedTenantRef.current = effectiveTenant;
    }

    loadDashboardData();

    return () => { aliveRef.current = false; };
  }, [isUserReady, effectiveTenant, loadDashboardData]);

  useEffect(() => {
    let cancelled = false;
    setMarketOnboarding(null);
    if (!effectiveTenant) return undefined;
    (async () => {
      try {
        const status = await probeMarketModule(effectiveTenant);
        if (cancelled || (status !== 'on' && status !== 'needs_setup' && status !== 'fault')) return;
        const lenses = await fetchMarketLenses(effectiveTenant);
        const lens = lenses?.default_lens || lenses?.lenses?.[0]?.lens_key;
        const data = await fetchMarketOnboarding(effectiveTenant, lens);
        if (cancelled) return;
        if (data?.state === 'empty' && data?.cta) {
          setMarketOnboarding(data);
        }
      } catch {
        /* module off or unavailable — no Home CTA */
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveTenant]);

  const normalizeCategory = (cat) => {
    if (!cat) return null;
    return cat.replace(/[\u2010\u2011\u2012\u2013\u2014\u2015]/g, '-').trim();
  };

  const filteredProcurement = useMemo(() => {
    console.log('🔔 HOME DEBUG: alertPreferences', alertPreferences);

    if (!alertPreferences) {
      console.log('🎯 HOME: No alert preferences, showing all procurement');
      return procurement;
    }

    const categories = expandAlertCategoriesForMatching(alertPreferences.live_deal_categories || []);
    console.log('🔔 HOME DEBUG: selected categories', categories);

    if (categories.length === 0) {
      console.log('🎯 HOME: No categories selected, showing all procurement');
      return procurement;
    }

    // Normalize hyphens for matching
    const normalizeCategory = (cat) => {
      if (!cat) return null;
      return cat.replace(/[\u2010\u2011\u2012\u2013\u2014\u2015]/g, '-').trim();
    };
  
    const normalizedCategories = categories.map(c => normalizeCategory(c));

    const filtered = procurement.filter(p => {
      const dealCategory = normalizeCategory(p.category);
      return dealCategory && normalizedCategories.includes(dealCategory);
    });

    console.log('🎯 HOME: Filtered procurement by alert preferences:', {
      totalProcurement: procurement.length,
      selectedCategories: categories,
      filteredCount: filtered.length
    });

    return filtered;
  }, [procurement, alertPreferences]);

  const planLoadingRef = useRef(false);

  const loadTodaysPlan = useCallback(async () => {
    if (planLoadingRef.current) {
      console.log('⏭️ Plan already loading, skipping');
      return;
    }
    const adminTenant = userRole === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
    const tenantId = resolveEffectiveTenant(adminTenant, currentTenant, contextUser, 'instructure');
    const cachedPlan = readPlanCache(tenantId);

    if (isInRateLimitCooldown()) {
      const remaining = getRemainingCooldownTime();
      console.log(`⏸️ Plan load: In cooldown (${formatRemainingTime(remaining)} remaining), skipping API call.`);

      if (cachedPlan && cachedPlan.plan) {
        console.log('✅ Using cached plan due to rate limit');
        setTodaysPlan(cachedPlan.plan);
      } else {
        console.log('⛔ No cached plan available during rate limit cooldown, setting empty plan.');
        const emptyPlan = { updatedAt: new Date().toISOString(), planByCategory: {}, flat: [] };
        setTodaysPlan(emptyPlan);
      }

      if (shouldShowRateLimitToast()) {
        notify({
          title: t('todaysPlanRateLimitActive'),
          description: t('todaysPlanCannotLoadRetry', { remaining: formatRemainingTime(remaining) }),
          variant: "default",
          duration: 8000
        });
        markRateLimitToastShown();
      }
      setIsPlanLoading(false);
      return;
    }

    if (cachedPlan && cachedPlan.isFresh) {
      console.log('✅ Using fresh cached plan');
      setTodaysPlan((prev) => {
        if (prev?.updatedAt && prev.updatedAt === cachedPlan.plan?.updatedAt) return prev;
        return cachedPlan.plan;
      });
      setIsPlanLoading(false);
      return;
    }

    planLoadingRef.current = true;

    if (!todaysPlanRef.current) {
      setIsPlanLoading(true);
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/todays-plan?tenant_id=${tenantId}&user_id=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId || 'default',
          'x-tenant-id': tenantId
        },
        credentials: 'include'
      });

      const ct = res.headers.get('Content-Type') || '';
      if (!res.ok || !ct.includes('application/json')) {
        console.log('Today\'s plan not available or non-JSON response');
        const emptyPlan = { updatedAt: new Date().toISOString(), planByCategory: {}, flat: [] };
        setTodaysPlan(emptyPlan);
        writePlanCache(emptyPlan, tenantId);
        return;
      }

      const data = await res.json();

      clearRateLimitCooldown();

      console.log('📦 Plan received from server:', {
        categories: Object.keys(data.planByCategory || {}),
        itemCounts: Object.entries(data.planByCategory || {}).map(([cat, items]) =>
          `${cat}: ${Array.isArray(items) ? items.length : 0}`
        ),
        flatCount: data.flat?.length || 0
      });

      setTodaysPlan(data);
      writePlanCache(data, tenantId);
    } catch (e) {
      console.error('Plan load error', e);
      const isRateLimit = e.response?.status === 429 || e.status === 429 || e.message?.includes('Rate limit');
      if (isRateLimit) {
        setRateLimitCooldown();
        if (cachedPlan && cachedPlan.plan) {
           setTodaysPlan(cachedPlan.plan);
           if (shouldShowRateLimitToast()) {
            notify({
                title: t('todaysPlanRateLimitReached'),
                description: t('todaysPlanShowingCached'),
                variant: "default",
                duration: 8000
            });
            markRateLimitToastShown();
           }
        } else {
           if (shouldShowRateLimitToast()) {
            notify({
                title: t('todaysPlanRateLimitReached'),
                description: t('todaysPlanCannotLoadWait'),
                variant: "destructive",
                duration: 15000
            });
            markRateLimitToastShown();
           }
        }
      } else {
        notify({ title: t('error'), description: t('todaysPlanCouldNotLoad'), variant: "destructive" });
      }

      const emptyPlan = { updatedAt: new Date().toISOString(), planByCategory: {}, flat: [] };
      setTodaysPlan(emptyPlan);
      writePlanCache(emptyPlan, tenantId);
    } finally {
      setIsPlanLoading(false);
      planLoadingRef.current = false;
    }
  }, [contextUser, userId, userRole, currentTenant, t]);

  const refreshTodaysPlan = useCallback(async () => {
    if (isPlanRefreshing) {
      console.log('⏭️ Refresh already in progress, skipping');
      return;
    }

    if (isInRateLimitCooldown()) {
      const remaining = getRemainingCooldownTime();
      console.log(`⏸️ Plan refresh: In cooldown (${formatRemainingTime(remaining)} remaining), skipping API call.`);
      if (shouldShowRateLimitToast()) {
        notify({
          title: t('todaysPlanRateLimitActive'),
          description: t('todaysPlanCannotRefreshRetry', { remaining: formatRemainingTime(remaining) }),
          variant: "default",
          duration: 8000
        });
        markRateLimitToastShown();
      }
      return;
    }

    try {
      setIsPlanRefreshing(true);
      const adminTenant = userRole === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
      const tenantId = resolveEffectiveTenant(adminTenant, currentTenant, contextUser, 'instructure');

      const res = await fetch(`${API_BASE_URL}/api/todays-plan/refresh?tenant_id=${tenantId}&user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId || 'default',
          'x-tenant-id': tenantId
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: userId || 'default',
          tenant_id: tenantId
        })
      });
      const ct = res.headers.get('Content-Type') || '';
      if (!res.ok || !ct.includes('application/json')) {
        console.log('Today\'s plan refresh not available or non-JSON response');
        const emptyPlan = { updatedAt: new Date().toISOString(), planByCategory: {}, flat: [] };
        setTodaysPlan(emptyPlan);
        writePlanCache(emptyPlan, tenantId);
        return;
      }
      const data = await res.json();

      clearRateLimitCooldown();

      console.log('📦 Plan received from server:', {
        categories: Object.keys(data.planByCategory || {}),
        itemCounts: Object.entries(data.planByCategory || {}).map(([cat, items]) =>
          `${cat}: ${Array.isArray(items) ? items.length : 0}`
        ),
        flatCount: data.flat?.length || 0
      });

      setTodaysPlan(data);
      writePlanCache(data, tenantId);
      notify({
        title: t('todaysPlanRefreshed'),
        description: t('todaysPlanRefreshedDescription'),
      });
    } catch (e) {
      console.error('Plan refresh error', e);
      const isRateLimit = e.response?.status === 429 || e.status === 429 || e.message?.includes('Rate limit');
      if (isRateLimit) {
        setRateLimitCooldown();
        if (shouldShowRateLimitToast()) {
            notify({
                title: t('todaysPlanRateLimitReached'),
                description: t('todaysPlanRefreshRateLimit'),
                variant: "destructive",
                duration: 8000
            });
            markRateLimitToastShown();
        }
      } else {
        notify({
          title: t('todaysPlanRefreshFailedTitle'),
          description: t('todaysPlanRefreshFailed'),
          variant: "destructive"
        });
      }
    } finally {
      setIsPlanRefreshing(false);
    }
  }, [contextUser, userId, userRole, isPlanRefreshing, currentTenant, t]);

  const handleTogglePursuit = useCallback(async (personaId, nextOn) => {
    if (isInRateLimitCooldown()) {
      const remaining = getRemainingCooldownTime();
      console.log(`⏸️ Pursuit toggle: In cooldown (${formatRemainingTime(remaining)} remaining), skipping API call.`);
      if (shouldShowRateLimitToast()) {
        notify({
          title: "⏸️ Rate limit active",
          description: `Cannot update pursuit status. Will retry automatically in ${formatRemainingTime(remaining)}.`,
          variant: "default",
          duration: 8000
        });
        markRateLimitToastShown();
      }
      return;
    }

    // Optimistic UI update
    setTodaysPlan(prev => patchPlanTracked(prev, personaId, nextOn));
    setPeople(prevPeople =>
      prevPeople.map(p =>
        (p.id === personaId || p.person_id === personaId)
          ? { ...p, add_to_pursuit: nextOn }
          : p
      )
    );

    try {
      const adminTenant = userRole === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
      const tenantId = resolveEffectiveTenant(adminTenant, currentTenant, contextUser, 'instructure');

      const url = `${API_BASE_URL}/api/pursuit/${encodeURIComponent(personaId)}`;

      let response;
      if (nextOn) {
        response = await fetch(`${url}?user_id=${userId}&tenant_id=${tenantId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ user_id: userId, tenant_id: tenantId })
        });
      } else {
        response = await fetch(`${url}?user_id=${userId}&tenant_id=${tenantId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
      }

      if (!response.ok && response.status !== 404) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Pursuit toggle failed: ${response.status}`);
      }

      clearRateLimitCooldown();
      console.log('✅ Pursuit toggle successful');

      // Clear home cache so it refreshes with new status
      try {
        sessionStorage.removeItem(HOME_CACHE_KEY);
      } catch {}

    } catch (e) {
      console.error('Pursuit toggle error:', e);
      
      // Don't revert UI for network errors (endpoint not implemented yet)
      if (e.message?.includes('Failed to fetch')) {
        console.log('⚠️ Backend endpoint not available, keeping UI state');
        return;
      }
      
      const isRateLimit = e.response?.status === 429 || e.status === 429 || e.message?.includes('Rate limit');
      if (isRateLimit) {
        setRateLimitCooldown();
        if (shouldShowRateLimitToast()) {
          notify({
            title: "⚠️ Rate limit reached",
            description: "Couldn't update Pursuit due to rate limit. Will retry automatically in 20 minutes.",
            variant: "destructive",
            duration: 8000
          });
          markRateLimitToastShown();
        }
      } else {
        notify({ title: t('couldNotUpdatePursuit'), description: e.message || t('pleaseTryAgain'), variant: "destructive" });
      }

      // Revert optimistic update
      setTodaysPlan(prev => patchPlanTracked(prev, personaId, !nextOn));
      setPeople(prevPeople =>
        prevPeople.map(p =>
          (p.id === personaId || p.person_id === personaId)
            ? { ...p, add_to_pursuit: !nextOn }
            : p
        )
      );
    }
  }, [contextUser, userId, userRole, currentTenant, t]);

  const handleAcknowledge = useCallback(async (item) => {
    const key = planItemKey(item);

    if (isInRateLimitCooldown()) {
      const remaining = getRemainingCooldownTime();
      console.log(`⏸️ Acknowledge: In cooldown (${formatRemainingTime(remaining)} remaining), skipping API call.`);
      if (shouldShowRateLimitToast()) {
        notify({
          title: "⏸️ Rate limit active",
          description: `Cannot acknowledge item. Will retry automatically in ${formatRemainingTime(remaining)}.`,
          variant: "default",
          duration: 8000
        });
        markRateLimitToastShown();
      }
      setAckSet(prev => {
        const next = addPlanItemToAckSet(prev, item);
        writeAckSet(userId, next);
        return next;
      });
      return;
    }

    setAckSet(prev => {
      const next = addPlanItemToAckSet(prev, item);
      writeAckSet(userId, next);
      return next;
    });

    try {
      const adminTenant = userRole === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
      const tenantId = resolveEffectiveTenant(adminTenant, currentTenant, contextUser, 'instructure');      
      console.log('🔍 Acknowledge Debug:', {
        currentTenant,
        userTenantId: userTenantId,
        finalTenantId: tenantId,
        personName: item.personaName || item.personName,
        personId: item.personaId || item.person_id
      });
      
      const headers = {
        'Content-Type': 'application/json'
      };
      if (userId) headers['x-user-id'] = userId;
      headers['x-tenant-id'] = tenantId;

      const personaId = item.personaId || item.person_id || item.persona_id;
      
      console.log('📡 Sending acknowledge request:', {
        itemKey: key,
        personaId,
        personName: item.personaName,
        tenantId
      });

      const res = await fetch(`${API_BASE_URL}/api/todays-plan/ack?user_id=${userId}&tenant_id=${tenantId}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ 
          itemKey: key,
          personaId,
          personaName: item.personaName,
          accountName: item.accountName,
          tenant_id: tenantId,
          user_id: userId
        })
      });

      clearRateLimitCooldown();

      if (!res.ok && res.status !== 404) {
        console.warn('Could not persist acknowledgment to backend:', res.status, res.statusText);
      }
    } catch (e) {
      console.log('Acknowledgment saved locally (backend sync pending)', e);
      const isRateLimit = e.response?.status === 429 || e.status === 429 || e.message?.includes('Rate limit');
      if (isRateLimit) {
        setRateLimitCooldown();
        if (shouldShowRateLimitToast()) {
            notify({
                title: t('todaysPlanRateLimitReached'),
                description: t('todaysPlanAckRateLimit'),
                variant: "default",
                duration: 8000
            });
            markRateLimitToastShown();
        }
      } else {
        notify({ title: t('error'), description: t('todaysPlanCouldNotSaveAck'), variant: "destructive" });
      }
    }
  }, [userId, userTenantId, currentTenant, t]);

  useEffect(() => {
    if (userId) {
      loadTodaysPlan();
    }
  }, [userId, userTenantId, currentTenant, loadTodaysPlan]);

  const countryOptions = useMemo(() => {
    const set = new Set();
    accounts.forEach(a => {
      if (a?.country) set.add(a.country);
    });
    const opts = Array.from(set).sort((a, b) => a.localeCompare(b));
    return [{ label: t('allCountries'), value: "all" }, ...opts.map(c => ({ label: c, value: c }))];
  }, [accounts, t]);

  const handleCountryChange = useCallback((next) => {
    setFilters(prev => ({ ...prev, country: next }));
  }, []);

  const filteredAccounts = useMemo(() => {
    const adminTenant = userRole === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
    const effectiveTenantLocal = resolveEffectiveTenant(adminTenant, currentTenant, contextUser, 'instructure');
    const hongKongAccounts = [
      'City University of Hong Kong (CityU)',
      'The Hong Kong University of Science and Technology (HKUST)'
    ];
    
    const now = new Date();
    const twelveWeeksAgo = subWeeks(now, 12);
    
    const accountsWithMomentum = accounts.map(acc => {
      const accountMomentum = momentumScores.filter(m => {
        if (!m.weekly_period) return false;
        
        const institutionMatch = m.institution_name === acc.account_name;
        
        let dateValid = false;
        try {
          const periodDate = parseISO(m.weekly_period);
          dateValid = !isNaN(periodDate.getTime()) && isAfter(periodDate, twelveWeeksAgo);
        } catch (e) {
          console.warn('Date parse error for momentum:', m.weekly_period, e);
        }
        
        return institutionMatch && dateValid;
      });
      
      const momentum_12w = accountMomentum.reduce((sum, m) => sum + (m.momentum_score_change || 0), 0);
      
      return {
        ...acc,
        momentum_12w
      };
    });

    const filtered = accountsWithMomentum.filter(acc => {
      if (effectiveTenantLocal === 'instructure') {
        if (hongKongAccounts.includes(acc.account_name)) {
          return false;
        }
      }
      
      if (filters.country === "all") {
        return true;
      }
      
      if (acc.country && acc.country.toLowerCase() === filters.country.toLowerCase()) {
        return true;
      }
      
      return false;
    });
    
    return filtered;
  }, [accounts, momentumScores, filters.country, currentTenant, userTenantId]);
  
  // Match Market.jsx approach: use direct account_name comparison (faster)
  const filteredAccountNames = useMemo(() => {
    return new Set(
      filteredAccounts
        .map(a => normalizeAccountName(a.account_name))
        .filter(Boolean)
    );
  }, [filteredAccounts]);
  
  const filteredPeople = useMemo(() => {
    const adminTenant = userRole === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
    const effectiveTenantLocal = resolveEffectiveTenant(adminTenant, currentTenant, contextUser, 'instructure');
    const hongKongAccounts = [
      'City University of Hong Kong (CityU)',
      'The Hong Kong University of Science and Technology (HKUST)'
    ];

    console.log('👥 Home filteredPeople input (Market-style):', {
      peopleCount: people.length,
      filteredAccountNamesCount: filteredAccountNames.size,
      samplePeople: people.slice(0, 3).map(p => ({
        name: p.first_name,
        account_name: p.account_name
      })),
      sampleAccountNames: [...filteredAccountNames].slice(0, 5)
    });
    
    // Match Market.jsx filtering pattern: direct account_name comparison
    const result = people.filter(p => {
      if (effectiveTenantLocal === 'instructure') {
        if (hongKongAccounts.includes(p.account_name)) {
          return false;
        }
      }
      
      if (p.account_name && filteredAccountNames.has(normalizeAccountName(p.account_name))) {
        return true;
      }
      
      return false;
    });
    
    const seen = new Map();
    result.forEach(p => {
      const key = p.person_id || p.id;
      const existing = seen.get(key);
      
      // Keep if: no existing, OR this one is from current tenant, OR this one has pursuit enabled
      if (!existing || 
          p.tenant_id === effectiveTenantLocal || 
          (p.add_to_pursuit && !existing.add_to_pursuit)) {
        seen.set(key, p);
      }
    });
    
    console.log('📊 Home filtered people (Market-style):', {
      total: people.length,
      afterFilter: result.length,
      afterDedup: seen.size,  // ← ADD THIS
      accountFilter: filters.country,
      tenant: effectiveTenantLocal,
      sampleFiltered: result.slice(0, 3).map(p => ({
        name: `${p.first_name} ${p.last_name}`,
        account: p.account_name
      })),
      sampleFilteredAccounts: [...filteredAccountNames].slice(0, 5)
    });
    
    return Array.from(seen.values());
  }, [people, filteredAccountNames, currentTenant, userTenantId]);

  const trackedPeople = useMemo(() => {
    return (filteredPeople || []).filter(p => p.add_to_pursuit === true);
  }, [filteredPeople]);

  const planPersonaIds = useMemo(() => {
    const ids = new Set();
    const buckets = todaysPlan?.planByCategory || {};
    Object.values(buckets).forEach(items => {
      (items || []).forEach(it => {
        if (it?.personaId) ids.add(it.personaId);
        if (it?.person_id) ids.add(it.person_id);
      });
    });
    return ids;
  }, [todaysPlan]);

  const allPursuitPeople = useMemo(() => {
    return trackedPeople;  // Show ALL pursuit personas, not just those outside the plan
  }, [trackedPeople]);

  const allPersonIds = useMemo(() => {
    const ids = new Set();
    filteredPeople.forEach(p => {
      if (p.person_id) ids.add(p.person_id);
      if (p.id) ids.add(p.id);
    });
    allPursuitPeople.forEach(p => {
      if (p.person_id) ids.add(p.person_id);
      if (p.id) ids.add(p.id);
    });
    return Array.from(ids);
  }, [filteredPeople, allPursuitPeople]);

  const limitedPlan = useMemo(() => {
    const result = top5PlanByImpact(todaysPlan, ackSet);
    
    console.log('🎯 Limited plan after filtering:', {
      ackSetSize: ackSet.size,
      ackSetKeys: [...ackSet].slice(0, 3),
      resultCategories: Object.keys(result),
      resultCounts: Object.entries(result).map(([cat, items]) =>
        `${cat}: ${Array.isArray(items) ? items.length : 0}`
      )
    });
    
    return result;
  }, [todaysPlan, ackSet]);

  const todayLabel = useMemo(() => {
    const language = resolvePlatformUiLanguage(contextUser, currentTenant);
    const localeMap = {
      'en': 'en-US',
      'es': 'es-ES',
      'pt-BR': 'pt-BR',
      'fr': 'fr-FR',
      'nl': 'nl-NL',
      'de': 'de-DE',
      'ar': 'ar-SA'
    };
    const locale = localeMap[language] || 'en-US';
    return new Date().toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' });
  }, [contextUser, currentTenant]);
  
  const greeting = useMemo(() => {
    const name = contextUser?.display_name || contextUser?.full_name;
    if (!name) return '';
    return getGreeting(name, t);
  }, [contextUser?.display_name, contextUser?.full_name, t]);

  const showSkeletons = isLoading && !hydratedFromCache && accounts.length === 0;

  const getPersonaLabel = (score) => {
    if (score == null) return { label: "N/A", cls: "bg-gray-100 text-gray-700" };
    const band = personaFitBand(score) ?? 'passive';
    return {
      label: PERSONA_FIT_LABELS[band],
      cls: {
        strategic: "bg-green-100 text-green-800",
        supportive: "bg-blue-100 text-blue-800",
        passive: "bg-gray-100 text-gray-800",
        lowFit: "bg-red-100 text-red-800",
      }[band],
    };
  };

  const handleOpenDraft = useCallback(async (stepId, task) => {
    // Access filteredPeople and filteredAccounts from component scope
    const people = filteredPeople || [];
    const accounts = filteredAccounts || [];
    if (!stepId) {
      notify({
        title: 'Error',
        description: 'Draft ID is missing',
        variant: 'destructive'
      });
      return;
    }

    try {
      const adminTenant = userRole === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
      const tenantId = resolveEffectiveTenant(adminTenant, effectiveTenant, contextUser, 'instructure');

      if (!userId) {
        notify({
          title: 'Error',
          description: 'User ID is missing',
          variant: 'destructive'
        });
        return;
      }

      // Fetch draft data
      const response = await fetch(
        `${API_BASE_URL}/api/outbox/${stepId}?tenant_id=${tenantId}&user_id=${userId}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(`Failed to load draft: ${response.status}`);
      }

      const draftData = await response.json();
      
      // Find person from people if available
      const personId = task?.person_id || task?.personId || draftData.person_id;
      const person = personId ? people.find(p => 
        (p.person_id === personId || p.id === personId)
      ) : null;
      
      // Find account from accounts if available
      const accountId = task?.account_id || task?.accountId || draftData.account_id;
      const account = accountId ? accounts.find(a => 
        (a.account_id === accountId || a.id === accountId)
      ) : null;
      
      // Normalize body_html to support both key styles
      const bodyHtml = draftData.body_html || draftData.bodyHtml || draftData.body || draftData.html || '';
      
      // Normalize sequence_id with explicit fallbacks
      const sequenceId = task?.sequence_id 
        || task?.sequenceId 
        || draftData.sequence_id 
        || draftData.sequenceId 
        || draftData.sequence?.id 
        || null;
      
      // Normalize stage with explicit fallbacks
      const stage = task?.stage 
        || task?.currentStage 
        || task?.stage_no 
        || task?.stageNo
        || draftData.stage 
        || draftData.currentStage
        || draftData.stage_no 
        || draftData.stageNo
        || null;
      
      // Normalize touch_number
      const touchNumber = task?.touch_number 
        || task?.touchNumber 
        || draftData.touch_number 
        || draftData.touchNumber 
        || null;
      
      // Use touch_number as fallback for stage if stage is not available
      const stageNoFinal = stage ?? touchNumber ?? null;
      
      // Debug logs to confirm source of truth
      console.log("🧪 TASK keys:", Object.keys(task || {}));
      console.log("🧪 DRAFT keys:", Object.keys(draftData || {}));
      console.log("🧪 DRAFT sequence/stage raw:", {
        task_sequence_id: task?.sequence_id,
        draft_sequence_id: draftData?.sequence_id,
        task_touch_number: task?.touch_number,
        draft_touch_number: draftData?.touch_number,
        draft_stage_no: draftData?.stage_no,
        draft_stage: draftData?.stage,
        computed_stage: stageNoFinal
      });
      
      // Normalize draft object for OutboxDrawer with all required fields
      const normalizedDraft = {
        ...draftData,
        // IDs
        id: draftData.id || stepId,
        step_id: stepId,
        stepId: stepId,
        draftId: stepId,
        // Tenant/User context
        tenantId: tenantId,
        userId: userId,
        // Channel
        channel: draftData.channel || task?.channel,
        // Person data
        person_id: personId || draftData.person_id,
        personId: personId || draftData.person_id,
        person: person || draftData.person || task?.person || (personId ? { id: personId, person_id: personId } : null),
        person_name: task?.person_name || draftData.person_name || person?.full_name || (person ? `${person.first_name} ${person.last_name}` : null),
        // Account data
        account_id: accountId || draftData.account_id,
        accountId: accountId || draftData.account_id,
        account: account || draftData.account || task?.account || (accountId ? { id: accountId, account_id: accountId } : null),
        account_name: task?.account_name || draftData.account_name || account?.account_name,
        // Sequence/Stage data - explicitly mapped
        sequence_id: sequenceId,
        sequenceId: sequenceId,
        sequence: sequenceId ? { id: sequenceId } : null,
        stage: stageNoFinal,
        stage_no: stageNoFinal,
        stageNo: stageNoFinal,
        currentStage: stageNoFinal,
        touch_number: touchNumber,
        touchNumber: touchNumber,
        // Contact info from task
        phone: task?.phone || draftData.phone,
        linkedin_url: task?.linkedin_url || task?.linkedinUrl || draftData.linkedin_url || draftData.linkedinUrl,
        // Message content
        subject: draftData.subject || '',
        body_html: bodyHtml,
        bodyHtml: bodyHtml,
        body: bodyHtml,
        html: bodyHtml,
        // Status
        status: draftData.status || 'draft'
      };
      
      // Debug log to confirm sequence_id mapping
      console.log("🧪 handleOpenDraft stage inputs:", {
        stepId,
        task_sequence_id: task?.sequence_id,
        task_sequenceId: task?.sequenceId,
        task_stage: task?.stage,
        task_stage_no: task?.stage_no,
        fetched_sequence_id: draftData?.sequence_id,
        fetched_sequenceId: draftData?.sequenceId,
        fetched_stage: draftData?.stage,
        fetched_stage_no: draftData?.stage_no,
        normalized_sequence_id: normalizedDraft.sequence_id,
        normalized_stage: normalizedDraft.stage
      });

      setSelectedTaskForDrawer(task);
      setSelectedDraftForDrawer(normalizedDraft);
    } catch (error) {
      console.error('Error loading draft:', error);
      notify({
        title: 'Error',
        description: error.message || 'Failed to load draft',
        variant: 'destructive'
      });
    }
  }, [effectiveTenant, userId, userTenantId, filteredPeople, filteredAccounts]);

  return (
    <PlaybookStatusProvider personIds={allPersonIds} currentTenant={effectiveTenant}>
      <div className="bg-gray-50 -m-8 -mt-8 min-h-screen">
        <div className="bg-white border-b mb-6 -mx-8">
          <div className="max-w-7xl mx-auto px-12 sm:px-8 md:px-10 py-8">
            {greeting && (
              <h1 className="text-3xl font-bold text-[#0A1A2F] mb-2">
                {greeting}
              </h1>
            )}
            <div className="flex flex-wrap items-center gap-x-3 md:gap-x-4 gap-y-2">
              <p className="text-lg text-[#5C6B7A]">{todayLabel}</p>
              {hydratedFromCache && isLoading && (
                <Badge variant="secondary" className="text-xs">{t('refreshing')}</Badge>
              )}
            </div>
            {marketOnboarding?.cta ? (
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-[#00C2C2]/30 bg-[#E6F8F8] px-4 py-3">
                <p className="text-sm text-[#0A1A2F]">
                  {marketOnboarding.cta.detail}
                </p>
                <Button asChild size="sm" className="bg-[#00C2C2] hover:bg-[#00A8A8] text-white shrink-0">
                  <Link to={createPageUrl((marketOnboarding.cta.href || '/Market').replace(/^\//, ''))}>
                    {marketOnboarding.cta.label || 'Choose your accounts'}
                  </Link>
                </Button>
              </div>
            ) : null}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-2 sm:px-6 py-0">
        <div className="space-y-8">

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
        <div className="w-full">
          <ActionCenterWithPlan
            momentumScores={momentumScores}
            personaActivities={personaActivities}
            procurement={filteredProcurement}
            plan={limitedPlan}
            updatedAt={todaysPlan?.updatedAt || null}
            isRefreshing={isPlanRefreshing}
            isPlanLoading={isPlanLoading}
            isHydrating={showSkeletons}
            onRefresh={refreshTodaysPlan}
            onAcknowledge={handleAcknowledge}
            startChatSession={startChatSessionWithTenant}
            onTogglePursuit={handleTogglePursuit}
            accounts={filteredAccounts}
            people={filteredPeople}
            currentTenant={effectiveTenant}
            alertPreferences={alertPreferences}
            onOpenDraft={handleOpenDraft}
          />
        </div>

        <div className="w-full space-y-4 md:space-y-6">
          {showSkeletons ? (
            <>
              <div className="h-48 bg-gray-100 rounded-md animate-pulse" />
              <div className="h-48 bg-gray-100 rounded-md animate-pulse" />
            </>
          ) : (
            <>
              <TopPersonasCard 
                personas={filteredPeople}
                country={filters.country}
                onCountryChange={handleCountryChange}
                countryOptions={countryOptions}
                currentTenant={effectiveTenant}
              />
              <TopAccountsCard 
                accounts={filteredAccounts}
                country={filters.country}
                onCountryChange={handleCountryChange}
                countryOptions={countryOptions}
                currentTenant={effectiveTenant}
              />
              {allPursuitPeople.length > 0 && (
                <div className="bg-white border rounded-lg p-3 md:p-4 shadow-md">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h2 className="text-base md:text-lg font-semibold text-[#0A1A2F]">{t('yourWatchlist')}</h2>
                      <Badge variant="secondary" className="text-xs">{allPursuitPeople.length}</Badge>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="p-1 rounded-full hover:bg-gray-100 transition-colors cursor-help">
                            <Info className="w-4 h-4 text-[#5C6B7A]" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-[#0A1A2F] text-white border-0 shadow-lg">
                          <p className="text-sm">{t('watchlistTooltip')}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {allPursuitPeople
                      .sort((a, b) => (b.persona_fit_score || 0) - (a.persona_fit_score || 0))
                      .slice(0, 8)
                      .map(p => {
                        const fullName = `${p.first_name} ${p.last_name}`;
                        // Support both id and person_id across tenant datasets.
                        const personIdForLink = p.person_id || p.id;
                        if (!personIdForLink) return null;
                        const personaLabel = getPersonaLabel(p.persona_fit_score);
                        return (
                          <li key={personIdForLink} className="py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                              <PersonAvatar
                                personId={p.person_id || p.id}
                                hostedPhotoUrl={p.hosted_photo_url}
                                linkedinPhotoUrl={p.profile_photo_url}
                                firstName={p.first_name}
                                lastName={p.last_name}
                                size="sm"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-[#0A1A2F] truncate flex flex-wrap items-center gap-1.5">
                                  <Link
                                    to={createPageUrl(`PersonProfile?id=${encodeURIComponent(personIdForLink)}`)}
                                    className="hover:text-[#00C2C2] hover:underline transition-colors"
                                  >
                                    {fullName}
                                  </Link>
                                  <PlaybookIndicator type="person" id={personIdForLink} tenantId={effectiveTenant || userTenantId} userId={userId} />
                                  {p.persona_fit_score != null && (
                                    <Badge className={`${personaLabel.cls} text-[10px] h-5 px-1.5 rounded-full whitespace-nowrap`}>
                                      {personaLabel.label} ({p.persona_fit_score.toFixed(0)})
                                    </Badge>
                                  )}
                                  {p.worked_for_customer && (
                                    <TooltipProvider delayDuration={0}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button className="cursor-help" onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }}>
                                            <Badge className="bg-green-100 text-green-700 text-[10px] h-5 px-1.5 rounded-full whitespace-nowrap">
                                              Former Client
                                            </Badge>
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent 
                                          side="bottom" 
                                          align="center"
                                          sideOffset={8}
                                          className="max-w-xs bg-white text-gray-900 border border-gray-200 shadow-lg"
                                        >
                                          <p className="text-sm">
                                            {p.client_name || 'Previously worked for one of our customers'}
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                                <div className="text-xs text-[#5C6B7A] truncate">{p.role_title}</div>
                                <div className="text-xs text-[#5C6B7A] truncate">{p.account_name}</div>
                              </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0 sm:ml-auto">
                              <Link
                                to={createPageUrl(`PersonProfile?id=${encodeURIComponent(personIdForLink)}`)}
                                className="text-xs border rounded px-3 py-1.5 hover:bg-gray-50 whitespace-nowrap"
                              >
                                {t('open')}
                              </Link>
                              <button
                                className="text-xs border rounded px-3 py-1.5 hover:bg-gray-50 whitespace-nowrap"
                                onClick={() => handleTogglePursuit(personIdForLink, false)}
                              >
                                {t('untrack')}
                              </button>
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                  {allPursuitPeople.length > 8 && (
                    <div className="mt-2 text-xs text-gray-500">+ {allPursuitPeople.length - 8} {t('moreTracked')}</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
        </div>
        </div>
      </div>

      {selectedDraftForDrawer && (
        <OutboxDrawer
          draft={selectedDraftForDrawer}
          tenantId={effectiveTenant}
          userId={userId}
          onClose={() => {
            setSelectedDraftForDrawer(null);
            setSelectedTaskForDrawer(null);
          }}
          onSent={() => {
            // Optional: mark task as complete after sending
            // if (selectedTaskForDrawer?.id) {
            //   // Handle task completion if needed
            // }
            setSelectedDraftForDrawer(null);
            setSelectedTaskForDrawer(null);
          }}
        />
      )}
    </PlaybookStatusProvider>
  );
}